import type { RouteRecordRaw } from 'vue-router'

import type { Capability } from '@/lib/permissions'

/**
 * Route metadata is declared here rather than derived from the file tree, so
 * the access rules for the whole application are readable in one place.
 */
declare module 'vue-router' {
  interface RouteMeta {
    /** No session required. */
    public?: boolean
    /** Redirect to the dashboard when already signed in. */
    guestOnly?: boolean
    /** Capability required to enter, checked against lib/permissions. */
    capability?: Capability
    /** Only admins and instructors. */
    staffOnly?: boolean
    titleKey?: string
  }
}

export const routes: RouteRecordRaw[] = [
  // -- Authentication ---------------------------------------------------------
  {
    path: '/login',
    name: 'login',
    component: () => import('@/pages/auth/LoginPage.vue'),
    meta: { public: true, guestOnly: true, titleKey: 'auth.loginTitle' },
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@/pages/auth/RegisterPage.vue'),
    meta: { public: true, guestOnly: true, titleKey: 'auth.registerTitle' },
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/pages/auth/ForgotPasswordPage.vue'),
    meta: { public: true, guestOnly: true, titleKey: 'auth.forgotTitle' },
  },
  {
    // Reached from an email link that carries a recovery session, so it must be
    // reachable while signed in.
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('@/pages/auth/ResetPasswordPage.vue'),
    meta: { public: true, titleKey: 'auth.resetTitle' },
  },

  // -- Public -----------------------------------------------------------------
  {
    path: '/verify/:token',
    name: 'verify-certificate',
    component: () => import('@/pages/public/VerifyCertificatePage.vue'),
    meta: { public: true, titleKey: 'certificates.verifyTitle' },
  },
  {
    path: '/p/courses/:id',
    name: 'public-course',
    redirect: (to) => ({
      name: 'course-detail',
      params: to.params,
      query: to.query,
      hash: to.hash,
    }),
    meta: { public: true, titleKey: 'nav.courses' },
  },
  {
    path: '/p/courses/:id/learn/:lessonId?',
    name: 'public-course-player',
    redirect: (to) => ({
      name: 'course-player',
      params: to.params,
      query: to.query,
      hash: to.hash,
    }),
    meta: { public: true, titleKey: 'player.lesson' },
  },
  {
    path: '/p/journeys/:id',
    name: 'public-journey',
    component: () => import('@/pages/public/PublicJourneyPage.vue'),
    meta: { public: true, titleKey: 'nav.journeys' },
  },
  {
    path: '/design',
    name: 'design',
    component: () => import('@/pages/design/DesignSystemPage.vue'),
    meta: { staffOnly: true, titleKey: 'admin.designTitle' },
  },

  {
    path: '/',
    name: 'landing',
    component: () => import('@/pages/public/LandingPage.vue'),
    meta: { public: true, titleKey: 'landing.metaTitle' },
  },

  // -- Learner ----------------------------------------------------------------
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('@/pages/learner/DashboardPage.vue'),
    meta: { titleKey: 'nav.dashboard' },
  },
  {
    path: '/courses',
    name: 'courses',
    component: () => import('@/pages/learner/CoursesPage.vue'),
    meta: { public: true, titleKey: 'nav.courses' },
  },
  {
    path: '/courses/:id',
    name: 'course-detail',
    component: () => import('@/pages/learner/CourseDetailPage.vue'),
    meta: { public: true, titleKey: 'nav.courses' },
  },
  {
    // The player has its own focused layout, so it is not nested under the
    // learner shell.
    path: '/courses/:id/learn/:lessonId?',
    name: 'course-player',
    component: () => import('@/pages/learner/CoursePlayerPage.vue'),
    meta: { public: true, titleKey: 'player.lesson' },
  },
  {
    path: '/journeys',
    name: 'journeys',
    component: () => import('@/pages/learner/JourneysPage.vue'),
    meta: { public: true, titleKey: 'nav.journeys' },
  },
  {
    path: '/journeys/:id',
    name: 'journey-detail',
    component: () => import('@/pages/learner/JourneyDetailPage.vue'),
    meta: { public: true, titleKey: 'nav.journeys' },
  },
  {
    path: '/certificates',
    name: 'certificates',
    component: () => import('@/pages/learner/CertificatesPage.vue'),
    meta: { titleKey: 'nav.certificates' },
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import('@/pages/learner/ProfilePage.vue'),
    meta: { titleKey: 'nav.profile' },
  },

  {
    path: '/setup',
    name: 'setup-admin',
    component: () => import('@/pages/admin/SetupAdminPage.vue'),
    meta: { titleKey: 'setup.title' },
  },

  // -- Administration ---------------------------------------------------------
  {
    path: '/admin',
    name: 'admin-dashboard',
    component: () => import('@/pages/admin/AdminDashboardPage.vue'),
    meta: { staffOnly: true, titleKey: 'nav.adminDashboard' },
  },
  {
    path: '/admin/courses',
    name: 'admin-courses',
    component: () => import('@/pages/admin/AdminCoursesPage.vue'),
    meta: { staffOnly: true, titleKey: 'nav.adminCourses' },
  },
  {
    path: '/admin/courses/new',
    name: 'admin-course-new',
    component: () => import('@/pages/admin/AdminCourseNewPage.vue'),
    meta: { capability: 'course.create', titleKey: 'admin.newCourse' },
  },
  {
    path: '/admin/courses/:id/edit',
    name: 'admin-course-edit',
    component: () => import('@/pages/admin/AdminCourseEditPage.vue'),
    meta: { staffOnly: true, titleKey: 'admin.editCourse' },
  },
  {
    path: '/admin/journeys',
    name: 'admin-journeys',
    component: () => import('@/pages/admin/AdminJourneysPage.vue'),
    meta: { capability: 'journey.manage', titleKey: 'nav.adminJourneys' },
  },
  {
    path: '/admin/journeys/new',
    name: 'admin-journey-new',
    component: () => import('@/pages/admin/AdminJourneyEditPage.vue'),
    meta: { capability: 'journey.manage', titleKey: 'admin.newJourney' },
  },
  {
    path: '/admin/journeys/:id/edit',
    name: 'admin-journey-edit',
    component: () => import('@/pages/admin/AdminJourneyEditPage.vue'),
    meta: { capability: 'journey.manage', titleKey: 'admin.editJourney' },
  },
  {
    path: '/admin/users',
    name: 'admin-users',
    component: () => import('@/pages/admin/AdminUsersPage.vue'),
    meta: { capability: 'user.manage', titleKey: 'nav.adminUsers' },
  },
  {
    path: '/admin/reports',
    name: 'admin-reports',
    component: () => import('@/pages/admin/AdminReportsPage.vue'),
    meta: { capability: 'report.view', titleKey: 'nav.adminReports' },
  },
  {
    path: '/admin/settings',
    name: 'admin-settings',
    component: () => import('@/pages/admin/AdminSettingsPage.vue'),
    meta: { capability: 'settings.manage', titleKey: 'nav.adminSettings' },
  },

  // -- Fallbacks --------------------------------------------------------------
  {
    path: '/forbidden',
    name: 'forbidden',
    component: () => import('@/pages/public/ForbiddenPage.vue'),
    meta: { public: true, titleKey: 'states.forbiddenTitle' },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/pages/public/NotFoundPage.vue'),
    meta: { public: true, titleKey: 'states.notFoundTitle' },
  },
]
